"use client";

import Image from "next/image";
import { useState, useEffect, useRef } from "react";
import { createPortal } from "react-dom";
import { BoardingGallery } from "@/types/contentful";
import { getContentfulImageUrl } from "@/lib/contentful-image";
import { useLightbox } from "@/hooks/useLightbox";

interface CMSGalleryProps {
  boardingImages: BoardingGallery[];
}

export default function CMSBoardingSchoolGallery({
  boardingImages,
}: CMSGalleryProps) {
  const [lightboxOpen, setLightboxOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const [mounted, setMounted] = useState(false);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const isProgrammaticScrollRef = useRef(false);
  const scrollTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  // Ensure we're on the client side for portal
  useEffect(() => {
    setMounted(true);
  }, []);

  const handleClose = () => setLightboxOpen(false);

  // Use the reusable lightbox hook for scroll lock, escape, and popstate
  useLightbox(lightboxOpen, handleClose);

  // Keyboard navigation (arrow keys)
  useEffect(() => {
    if (!lightboxOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        setActiveIndex((prev) => (prev - 1 + boardingImages.length) % boardingImages.length);
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        setActiveIndex((prev) => (prev + 1) % boardingImages.length);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [lightboxOpen, boardingImages.length]);

  // Scroll to active image with smooth animation
  const scrollToIndex = (index: number) => {
    if (!scrollContainerRef.current) return;
    isProgrammaticScrollRef.current = true;
    const container = scrollContainerRef.current;
    const scrollWidth = container.clientWidth;
    container.scrollTo({
      left: index * scrollWidth,
      behavior: 'smooth'
    });
    
    setTimeout(() => {
      isProgrammaticScrollRef.current = false;
    }, 500);
  };

  // Update active index based on scroll position with debouncing
  useEffect(() => {
    if (!lightboxOpen || !scrollContainerRef.current) return;

    const container = scrollContainerRef.current;
    
    const handleScroll = () => {
      if (isProgrammaticScrollRef.current) return;
      
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
      
      scrollTimeoutRef.current = setTimeout(() => {
        const scrollLeft = container.scrollLeft;
        const scrollWidth = container.clientWidth;
        const calculatedIndex = Math.round(scrollLeft / scrollWidth);
        
        if (calculatedIndex !== activeIndex && 
            calculatedIndex >= 0 && 
            calculatedIndex < boardingImages.length) {
          setActiveIndex(calculatedIndex);
        }
      }, 150);
    };

    const handleScrollEnd = () => {
      if (isProgrammaticScrollRef.current) return;
      
      const scrollLeft = container.scrollLeft;
      const scrollWidth = container.clientWidth;
      const calculatedIndex = Math.round(scrollLeft / scrollWidth);
      
      if (calculatedIndex !== activeIndex && 
          calculatedIndex >= 0 && 
          calculatedIndex < boardingImages.length) {
        setActiveIndex(calculatedIndex);
      }
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    container.addEventListener('scrollend', handleScrollEnd, { passive: true } as AddEventListenerOptions);
    
    return () => {
      container.removeEventListener('scroll', handleScroll);
      container.removeEventListener('scrollend', handleScrollEnd);
      if (scrollTimeoutRef.current) {
        clearTimeout(scrollTimeoutRef.current);
      }
    };
  }, [lightboxOpen, activeIndex, boardingImages.length]);

  // Scroll to active image when it changes (from button clicks)
  useEffect(() => {
    if (lightboxOpen && scrollContainerRef.current) {
      scrollToIndex(activeIndex);
    }
  }, [activeIndex, lightboxOpen]);

  // Initial scroll position when lightbox opens
  useEffect(() => {
    if (lightboxOpen && scrollContainerRef.current) {
      isProgrammaticScrollRef.current = true;
      const container = scrollContainerRef.current;
      const scrollWidth = container.clientWidth;
      container.scrollLeft = activeIndex * scrollWidth;
      
      setTimeout(() => {
        isProgrammaticScrollRef.current = false;
      }, 100);
    }
    // Only run when lightbox opens
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // Sort images by order field - define before use in effects
  const sortedImages = [...boardingImages].sort((a, b) => {
    const orderA = Number(a.fields.order) || 0;
    const orderB = Number(b.fields.order) || 0;
    return orderA - orderB;
  });

  // Initial preload when lightbox opens - load a batch of images
  useEffect(() => {
    if (!lightboxOpen || boardingImages.length === 0) return;
    
    const preloadImage = (src: string, priority: boolean = false) => {
      const img = new window.Image();
      if (priority && 'fetchPriority' in img) {
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'high';
      }
      img.src = src;
    };
    
    // Preload current image with high priority
    if (sortedImages[activeIndex]) {
      const currentUrl = getContentfulImageUrl(
        sortedImages[activeIndex].fields.image,
        { width: 1920, height: 1080, quality: 90, format: "webp" }
      );
      preloadImage(currentUrl, true);
    }
    
    // On lightbox open, aggressively preload 15 images ahead and 15 behind for instant navigation
    for (let i = 1; i <= 15; i++) {
      const nextIdx = (activeIndex + i) % sortedImages.length;
      const prevIdx = (activeIndex - i + sortedImages.length) % sortedImages.length;
      // First 5 images get high priority
      const isHighPriority = i <= 5;
      
      if (sortedImages[nextIdx]) {
        const nextUrl = getContentfulImageUrl(
          sortedImages[nextIdx].fields.image,
          { width: 1920, height: 1080, quality: 90, format: "webp" }
        );
        preloadImage(nextUrl, isHighPriority);
      }
      
      if (sortedImages[prevIdx]) {
        const prevUrl = getContentfulImageUrl(
          sortedImages[prevIdx].fields.image,
          { width: 1920, height: 1080, quality: 90, format: "webp" }
        );
        preloadImage(prevUrl, isHighPriority);
      }
    }
    // Only run when lightbox opens, not on activeIndex change
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [lightboxOpen]);

  // Preload adjacent images in lightbox for faster navigation
  useEffect(() => {
    if (!lightboxOpen || boardingImages.length === 0) return;

    const preloadImage = (src: string, priority: boolean = false) => {
      const img = new window.Image();
      if (priority && 'fetchPriority' in img) {
        (img as HTMLImageElement & { fetchPriority?: string }).fetchPriority = 'high';
      }
      img.src = src;
    };

    // Aggressively preload next 8 and previous 8 images for instant navigation
    const imagesToPreload: { src: string; priority: boolean }[] = [];
    
    // Preload next images (first 3 with high priority)
    for (let i = 1; i <= 8; i++) {
      const nextIndex = (activeIndex + i) % sortedImages.length;
      if (sortedImages[nextIndex]) {
        const url = getContentfulImageUrl(
          sortedImages[nextIndex].fields.image,
          { width: 1920, height: 1080, quality: 90, format: "webp" }
        );
        imagesToPreload.push({ src: url, priority: i <= 3 });
      }
    }
    
    // Preload previous images (first 3 with high priority)
    for (let i = 1; i <= 8; i++) {
      const prevIndex = (activeIndex - i + sortedImages.length) % sortedImages.length;
      if (sortedImages[prevIndex] && prevIndex !== activeIndex) {
        const url = getContentfulImageUrl(
          sortedImages[prevIndex].fields.image,
          { width: 1920, height: 1080, quality: 90, format: "webp" }
        );
        imagesToPreload.push({ src: url, priority: i <= 3 });
      }
    }
    
    // Preload all images in parallel with appropriate priority
    imagesToPreload.forEach(({ src, priority }) => preloadImage(src, priority));
  }, [lightboxOpen, activeIndex, sortedImages]);

  const handleOpen = (idx: number) => {
    setActiveIndex(idx);
    setLightboxOpen(true);
  };

  if (!boardingImages || boardingImages.length === 0) {
    return (
      <section className="mt-12 w-full max-w-4xl mx-auto">
        <div className="bg-white rounded-2xl shadow-lg p-8 text-center">
          <div className="w-16 h-16 bg-yellow-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <svg
              className="w-8 h-8 text-yellow-600"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L3.732 16.5c-.77.833.192 2.5 1.732 2.5z"
              />
            </svg>
          </div>
          <h3 className="text-xl font-semibold text-gray-800 mb-2">
            Content Management System Setup Required
          </h3>
          <p className="text-gray-600 mb-4">
            The boarding school gallery content type needs to be created in
            Contentful before this section can display boarding school images.
          </p>
          <div className="text-sm text-gray-500 bg-gray-50 rounded-lg p-4">
            <strong>For administrators:</strong> Please create the
            "boardingGallery" content type in Contentful with title, image, and
            order fields.
          </div>
        </div>
      </section>
    );
  }

  return (
    <div className="mt-12 w-full max-w-4xl mx-auto">
      <div className="grid grid-cols-3 gap-4 mb-4">
        {sortedImages.slice(0, 3).map((boardingImage, idx) => (
          <button
            key={boardingImage.sys.id}
            className="rounded overflow-hidden shadow-sm focus:outline-hidden"
            onClick={() => handleOpen(idx)}
            aria-label="Open image in lightbox"
          >
            <Image
              src={getContentfulImageUrl(boardingImage.fields.image, {
                width: 500,
                height: 333,
                quality: 85,
                format: "webp",
              })}
              alt={
                boardingImage.fields.title?.toString() ||
                `Boarding facility ${idx + 1}`
              }
              width={500}
              height={333}
              className="object-cover w-full h-full"
              loading={idx === 0 ? "eager" : "lazy"}
              sizes="(max-width: 600px) 50vw, 500px"
            />
          </button>
        ))}
      </div>
      <div className="grid grid-cols-3 gap-4 mb-4">
        {sortedImages.slice(3, 6).map((boardingImage, idx) => (
          <button
            key={boardingImage.sys.id}
            className="rounded overflow-hidden shadow-sm focus:outline-hidden"
            onClick={() => handleOpen(idx + 3)}
            aria-label="Open image in lightbox"
          >
            <Image
              src={getContentfulImageUrl(boardingImage.fields.image, {
                width: 500,
                height: 333,
                quality: 85,
                format: "webp",
              })}
              alt={
                boardingImage.fields.title?.toString() ||
                `Boarding facility ${idx + 4}`
              }
              width={500}
              height={333}
              className="object-cover w-full h-full"
              loading={idx === 0 ? "eager" : "lazy"}
              sizes="(max-width: 600px) 50vw, 500px"
            />
          </button>
        ))}
      </div>
      {sortedImages.length > 6 && (
        <button
          className="w-full mt-4 rounded overflow-hidden shadow-sm focus:outline-hidden"
          onClick={() => handleOpen(6)}
          aria-label="Open image in lightbox"
        >
          <Image
            src={getContentfulImageUrl(sortedImages[6].fields.image, {
              width: 1280,
              height: 853,
              quality: 90,
              format: "webp",
            })}
            alt={
              sortedImages[6].fields.title?.toString() ||
              "Main boarding facility"
            }
            width={1280}
            height={853}
            className="object-cover w-full h-full"
          />
        </button>
      )}

      {/* Lightbox Modal - Rendered via Portal to escape stacking context */}
      {lightboxOpen &&
        mounted &&
        createPortal(
          <div
            className="fixed inset-0 z-50 bg-black bg-opacity-80 transition-opacity duration-300"
            onClick={handleClose}
          >
            {/* Top blue bar - responsive */}
            <div
              className="absolute top-0 left-0 right-0 z-10 flex justify-between items-center p-3 md:p-4 bg-school-navy"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-white text-base md:text-lg font-medium">
                {activeIndex + 1} of {sortedImages.length}
              </div>
              <button
                className="text-white text-3xl md:text-4xl font-bold hover:text-yellow-400 transition-colors p-1 md:p-2"
                onClick={handleClose}
                aria-label="Close gallery"
              >
                ×
              </button>
            </div>

            {/* Horizontal scroll container with snap - Instagram/Facebook style */}
            <div
              ref={scrollContainerRef}
              className="gallery-scroll-container absolute inset-0 pt-16 pb-20 md:pb-16 lg:pb-14 overflow-x-auto overflow-y-hidden"
            >
              <div className="flex h-full" style={{ width: `${sortedImages.length * 100}vw` }}>
                {sortedImages.map((boardingImage, idx) => (
                  <div
                    key={`lightbox-${boardingImage.sys.id}`}
                    className="gallery-scroll-item shrink-0 flex items-center justify-center px-2"
                    style={{ width: '100vw', height: '100%' }}
                    onClick={handleClose}
                  >
                    <div
                      className="relative max-w-full max-h-full"
                      onClick={(e) => e.stopPropagation()}
                      style={{ 
                        width: 'fit-content',
                        height: 'fit-content',
                        maxWidth: '100%',
                        maxHeight: '100%'
                      }}
                    >
                      {/* Use native img for instant loading with preloaded images */}
                      <img
                        src={getContentfulImageUrl(
                          boardingImage.fields.image,
                          { width: 1920, height: 1080, quality: 90, format: "webp" }
                        )}
                        alt={
                          boardingImage.fields.title?.toString() ||
                          `Gallery image ${idx + 1}`
                        }
                        className="max-w-full max-h-full w-auto h-auto object-contain rounded"
                        loading={Math.abs(idx - activeIndex) <= 5 ? 'eager' : 'lazy'}
                        fetchPriority={Math.abs(idx - activeIndex) <= 3 ? 'high' : 'low'}
                        style={{ maxHeight: 'calc(100vh - 10rem)' }}
                        decoding="async"
                      />
                    </div>
                  </div>
                ))}
              </div>
            </div>

            {/* Bottom blue bar - reorganized layout for larger screens */}
            <div
              className="absolute bottom-0 left-0 right-0 z-10 p-2 md:p-3 bg-school-navy"
              onClick={(e) => e.stopPropagation()}
            >
              {/* Mobile/tablet layout: thumbnails above buttons */}
              <div className="lg:hidden">
                {/* Compact thumbnail strip */}
                <div className="flex gap-1 md:gap-2 justify-center overflow-x-auto pb-1 md:pb-2 mb-2">
                  {(() => {
                    // Show fewer thumbnails on mobile (6), more on larger screens (10)
                    const maxThumbs = 10; // We'll handle mobile with CSS hiding
                    const startIdx = Math.max(
                      0,
                      activeIndex - Math.floor(maxThumbs / 2)
                    );
                    const endIdx = Math.min(
                      sortedImages.length,
                      startIdx + maxThumbs
                    );
                    const visibleImages = sortedImages.slice(startIdx, endIdx);

                    return visibleImages.map((img, idx) => {
                      const actualIdx = startIdx + idx;
                      return (
                        <button
                          key={img.sys.id}
                          className={`w-10 h-10 md:w-12 md:h-12 relative border-2 shrink-0 ${
                            activeIndex === actualIdx
                              ? "border-yellow-400"
                              : "border-transparent"
                          } rounded overflow-hidden hover:border-yellow-200 transition-colors ${
                            idx >= 6 ? "hidden sm:block" : "" // Hide thumbnails 7-10 on mobile
                          }`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveIndex(actualIdx);
                          }}
                          aria-label={`View image ${actualIdx + 1}`}
                        >
                          <Image
                            src={getContentfulImageUrl(img.fields.image, {
                              width: 64,
                              height: 64,
                              quality: 80,
                              format: "webp",
                            })}
                            alt={
                              img.fields.title?.toString() ||
                              `Gallery image ${actualIdx + 1}`
                            }
                            fill
                            className="object-cover"
                            sizes="(max-width: 640px) 40px, 48px"
                          />
                        </button>
                      );
                    });
                  })()}

                  {/* Show indicator if there are more images - responsive text */}
                  {sortedImages.length > 6 && (
                    <div className="flex items-center px-2 md:px-3 text-gray-300 text-xs md:text-sm whitespace-nowrap">
                      <span className="sm:hidden">
                        +{sortedImages.length - 6} more
                      </span>
                      <span className="hidden sm:inline">
                        +{sortedImages.length - 10} more
                      </span>
                    </div>
                  )}
                </div>

                {/* Navigation buttons */}
                <div className="flex flex-col sm:flex-row gap-2 sm:justify-between w-full">
                  <button
                    className="w-full sm:w-auto text-black text-sm md:text-base px-3 md:px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveIndex(
                        (activeIndex - 1 + sortedImages.length) %
                          sortedImages.length
                      );
                    }}
                    disabled={sortedImages.length <= 1}
                    aria-label="Previous image"
                  >
                    ← Previous
                  </button>
                  <button
                    className="w-full sm:w-auto text-black text-sm md:text-base px-3 md:px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
                    onClick={(e) => {
                      e.stopPropagation();
                      setActiveIndex((activeIndex + 1) % sortedImages.length);
                    }}
                    disabled={sortedImages.length <= 1}
                    aria-label="Next image"
                  >
                    Next →
                  </button>
                </div>
              </div>

              {/* Desktop layout: single row with thumbnails between buttons */}
              <div className="hidden lg:flex items-center justify-between gap-4">
                {/* Previous button */}
                <button
                  className="text-black text-base px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex(
                      (activeIndex - 1 + sortedImages.length) %
                        sortedImages.length
                    );
                  }}
                  disabled={sortedImages.length <= 1}
                  aria-label="Previous image"
                >
                  ← Previous
                </button>

                {/* Thumbnails in center */}
                <div className="flex gap-2 justify-center overflow-x-auto flex-1 max-w-2xl">
                  {(() => {
                    const maxThumbs = 8; // Fewer for inline layout
                    const startIdx = Math.max(
                      0,
                      activeIndex - Math.floor(maxThumbs / 2)
                    );
                    const endIdx = Math.min(
                      sortedImages.length,
                      startIdx + maxThumbs
                    );
                    const visibleImages = sortedImages.slice(startIdx, endIdx);

                    return visibleImages.map((img, idx) => {
                      const actualIdx = startIdx + idx;
                      return (
                        <button
                          key={img.sys.id}
                          className={`w-12 h-12 relative border-2 shrink-0 ${
                            activeIndex === actualIdx
                              ? "border-yellow-400"
                              : "border-transparent"
                          } rounded overflow-hidden hover:border-yellow-200 transition-colors`}
                          onClick={(e) => {
                            e.stopPropagation();
                            setActiveIndex(actualIdx);
                          }}
                          aria-label={`View image ${actualIdx + 1}`}
                        >
                          <Image
                            src={getContentfulImageUrl(img.fields.image, {
                              width: 64,
                              height: 64,
                              quality: 80,
                              format: "webp",
                            })}
                            alt={
                              img.fields.title?.toString() ||
                              `Gallery image ${actualIdx + 1}`
                            }
                            fill
                            className="object-cover"
                            sizes="48px"
                          />
                        </button>
                      );
                    });
                  })()}

                  {/* Show indicator if there are more images */}
                  {sortedImages.length > 8 && (
                    <div className="flex items-center px-2 text-gray-300 text-sm whitespace-nowrap">
                      +{sortedImages.length - 8} more
                    </div>
                  )}
                </div>

                {/* Next button */}
                <button
                  className="text-black text-base px-4 py-2 bg-school-gold hover:bg-school-gold-dark rounded font-bold transition-colors disabled:opacity-50 disabled:cursor-not-allowed shrink-0"
                  onClick={(e) => {
                    e.stopPropagation();
                    setActiveIndex((activeIndex + 1) % sortedImages.length);
                  }}
                  disabled={sortedImages.length <= 1}
                  aria-label="Next image"
                >
                  Next →
                </button>
              </div>
            </div>
          </div>,
          document.body
        )}
    </div>
  );
}
